// Multi-word fuzzy search — a pure function with no external dependency. UI only, no pricing effect.
//
// Tokens may appear in any order and need not be contiguous, with typo tolerance. Each token gets
// a match level, best first: 0 = exact substring of the whole text, 1 = prefix of a word,
// 2 = fuzzy (bounded Levenshtein). The fuzzy threshold grows with token length.

/**
 * Lowercase plus NFD without diacritics — the canonical normalization for catalogue search.
 *
 * @param s The text to normalize.
 * @returns The lowercased, accent-free text.
 */
export function normalize(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/**
 * Bounded Levenshtein distance: the exact distance when it is ≤ `max`, otherwise `max + 1`.
 * Single-row DP that bails out as soon as the whole current row exceeds `max`.
 *
 * @param a First string.
 * @param b Second string.
 * @param max The largest distance of interest.
 * @returns The exact distance when ≤ `max`, else `max + 1`.
 */
export function levenshteinBounded(a: string, b: string, max: number): number {
  if (a === b) return 0;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > max) return max + 1;
  let prev: number[] = Array.from({ length: lb + 1 }, (_, j) => j);
  for (let i = 1; i <= la; i++) {
    const cur: number[] = [i];
    let minLine = i;
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const v = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + cost);
      cur.push(v);
      if (v < minLine) minLine = v;
    }
    if (minLine > max) return max + 1; // the whole row exceeds the threshold, no need to continue
    prev = cur;
  }
  return prev[lb]! <= max ? prev[lb]! : max + 1;
}

/**
 * Fuzzy tolerance by token length: 0 under 4 letters, 1 for 4-7, 2 for 8 or more. A token with a digit (a
 * reference or a size: `3027`) is never fuzzy: one wrong digit is another product (`3026`), not a typo.
 *
 * @param token The query token.
 * @returns The maximum edit distance allowed.
 */
function fuzzyThreshold(token: string): number {
  if (/\d/.test(token)) return 0;
  return token.length < 4 ? 0 : token.length <= 7 ? 1 : 2;
}

/**
 * Match level of a token against an entry's already-normalized words. Returns the first (best)
 * matching level, or `null` when none matches. Tokens under 4 letters, or with a digit, allow no fuzziness.
 *
 * @param token The normalized query token.
 * @param words The entry's normalized words.
 * @returns 0 (exact substring), 1 (word prefix), 2 (fuzzy), or null.
 */
export function matchToken(token: string, words: readonly string[]): number | null {
  if (!token) return 0;
  const fullText = words.join(" ");
  if (fullText.includes(token)) return 0; // exact substring of the whole text
  for (const w of words) if (w.startsWith(token)) return 1; // prefix of a word
  const threshold = fuzzyThreshold(token);
  if (threshold > 0) {
    for (const w of words) if (levenshteinBounded(token, w, threshold) <= threshold) return 2; // fuzzy
  }
  return null;
}

/** A searchable entry: the raw `text` (ref + description) is normalized internally. */
export interface SearchEntry {
  text: string;
}

/**
 * Multi-word fuzzy search. An entry is kept when every query token matches one of its words.
 * The score is the sum of match levels (lower is better), sorted by score then catalogue order.
 * An empty query returns the full list in catalogue order.
 *
 * @param query The raw search text.
 * @param entries The searchable entries, in catalogue order.
 * @returns The matching entries, best first.
 */
export function fuzzySearch<T extends SearchEntry>(query: string, entries: readonly T[]): T[] {
  const tokens = normalize(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [...entries];
  const kept: { e: T; score: number; order: number }[] = [];
  entries.forEach((e, order) => {
    const words = normalize(e.text).split(/\s+/).filter(Boolean);
    let score = 0;
    for (const t of tokens) {
      const n = matchToken(t, words);
      if (n == null) return; // a token matches nothing, so the entry is dropped
      score += n;
    }
    kept.push({ e, score, order: order });
  });
  kept.sort((a, b) => a.score - b.score || a.order - b.order);
  return kept.map((r) => r.e);
}
